import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import {
  API_BASE_URL,
  API_TIMEOUT,
  HTTP_STATUS,
  TOAST_DURATION,
} from "constants/index";
import { tokenStorage } from "utils/storage";
import { ApiError, ApiResponse } from "types/index";
import { useUIStore } from "store/uiStore";
import { authService } from "./auth.service";

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    requestId: string;
    skipGlobalLoading?: boolean;
  };
}

class ApiService {
  private axiosInstance: AxiosInstance;
  private isRefreshing = false;
  private refreshSubscribers: ((token: string) => void)[] = [];
  private activeRequests = new Set<string>();

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.setupInterceptors();
  }

  private logoutInProgress = false;

  private setupInterceptors(): void {
    this.axiosInstance.interceptors.request.use(
      (config: ExtendedAxiosRequestConfig) => {
        const token = tokenStorage.getAccessToken();
        if (token) {
          // Check if token is expired before making the request
          if (authService.isTokenExpired(token)) {
            // Token is expired, show toast and logout
            // Check if this is a manual logout to avoid showing message
            const isManualLogout =
              sessionStorage.getItem("__manual_logout__") === "true";
            if (!isManualLogout) {
              useUIStore.getState().showToast({
                type: "error",
                message: "Token expired. You will be logged out.",
                title: "Token Expired",
                duration: TOAST_DURATION.LONG,
              });
            }
            authService.clearSession();
            tokenStorage.clearTokens();
            // Return a rejected promise to prevent the request
            return Promise.reject(new Error("Token expired"));
          } else {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        const requestId = `${config.method?.toUpperCase()}_${
          config.url
        }_${Date.now()}`;
        const url = (config.url || "").toLowerCase();
        const method = (config.method || "get").toLowerCase();
        // Determine if this request should show the global loader
        const isPageList =
          url.endsWith("/getpagelist") || url.includes("/getpagelist?");
        const isCreateOrUpdate =
          method === "post" || method === "put" || method === "patch";
        const isPublicEmployee = url.includes("/employee/encrypted/");
        const shouldShowGlobal =
          isPageList || isCreateOrUpdate || isPublicEmployee;
        // Respect explicit flag; otherwise, only show for allow-listed routes above
        const explicitFlag = config.metadata?.skipGlobalLoading; // true => skip; false => force show
        const skipGlobalLoading =
          explicitFlag !== undefined ? explicitFlag : !shouldShowGlobal;
        config.metadata = { requestId, skipGlobalLoading };

        if (!skipGlobalLoading) {
          this.activeRequests.add(requestId);
          this.updateGlobalLoadingState();
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.axiosInstance.interceptors.response.use(
      (response) => {
        const requestId = (response.config as ExtendedAxiosRequestConfig)
          .metadata?.requestId;
        const skipGlobalLoading = (
          response.config as ExtendedAxiosRequestConfig
        ).metadata?.skipGlobalLoading;
        if (requestId && !skipGlobalLoading) {
          this.activeRequests.delete(requestId);
          this.updateGlobalLoadingState();
        }
        return response;
      },
      async (error: AxiosError) => {
        // Remove request from active requests
        const requestId = (error.config as ExtendedAxiosRequestConfig)?.metadata
          ?.requestId;
        const skipGlobalLoading = (error.config as ExtendedAxiosRequestConfig)
          ?.metadata?.skipGlobalLoading;
        if (requestId && !skipGlobalLoading) {
          this.activeRequests.delete(requestId);
          this.updateGlobalLoadingState();
        }

        const originalRequest = error.config as AxiosRequestConfig & {
          _retry?: boolean;
        };

        if (
          error.response?.status === HTTP_STATUS.UNAUTHORIZED &&
          !originalRequest._retry
        ) {
          const requestUrl = (originalRequest.url || "").toLowerCase();
          // Never try to refresh for auth endpoints; just propagate the error
          if (
            requestUrl.includes("/auth/signin") ||
            requestUrl.includes("/auth/login") ||
            requestUrl.includes("/auth/refresh")
          ) {
            return Promise.reject(this.handleError(error));
          }
          if (this.isRefreshing) {
            return new Promise((resolve) => {
              this.refreshSubscribers.push((token: string) => {
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                }
                resolve(this.axiosInstance(originalRequest));
              });
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const refreshToken = tokenStorage.getRefreshToken();
            if (!refreshToken) {
              // No refresh token: clear and propagate error without hard redirect
              // Check if this is a manual logout to avoid showing message
              if (!this.logoutInProgress) {
                this.logoutInProgress = true;
                const isManualLogout =
                  sessionStorage.getItem("__manual_logout__") === "true";
                if (!isManualLogout) {
                  useUIStore.getState().showToast({
                    type: "error",
                    message: "Token expired. You will be logged out.",
                    title: "Token Expired",
                    duration: TOAST_DURATION.LONG,
                  });
                }
                authService.clearSession();
                tokenStorage.clearTokens();
                setTimeout(() => {
                  this.logoutInProgress = false;
                }, 500);
              }
              return Promise.reject(this.handleError(error));
            }

            const response = await this.post<{ accessToken: string }>(
              "/auth/refresh",
              {
                refreshToken,
              }
            );

            const { accessToken } = response.data;
            tokenStorage.setTokens(accessToken, refreshToken);

            this.refreshSubscribers.forEach((callback) =>
              callback(accessToken)
            );
            this.refreshSubscribers = [];

            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            }

            return this.axiosInstance(originalRequest);
          } catch (refreshError) {
            // Check if this is a manual logout to avoid showing message
            const isManualLogout =
              sessionStorage.getItem("__manual_logout__") === "true";
            if (!isManualLogout) {
              useUIStore.getState().showToast({
                type: "error",
                message: "Token expired. You will be logged out.",
                title: "Token Expired",
                duration: TOAST_DURATION.LONG,
              });
            }
            authService.clearSession();
            tokenStorage.clearTokens();
            return Promise.reject(this.handleError(error as AxiosError));
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(this.handleError(error));
      }
    );
  }

  private updateGlobalLoadingState(): void {
    const hasActiveRequests = this.activeRequests.size > 0;
    const { setLoading } = useUIStore.getState();
    setLoading(hasActiveRequests, "");
  }

  private handleError(error: AxiosError): ApiError {
    const standardMessage =
      "An error occurred on the server. Please contact the administrator.";
    if (error.response) {
      const { data, status } = error.response;
      return {
        message:
          (data as any)?.error || (data as any)?.message || standardMessage,
        code: (data as any)?.code,
        statusCode: status,
        errors: (data as any)?.errors,
      };
    } else if (error.request) {
      return {
        message: standardMessage,
        code: "NETWORK_ERROR",
      };
    } else {
      return {
        message: error.message || standardMessage,
        code: "UNKNOWN_ERROR",
      };
    }
  }

  async get<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> =
      await this.axiosInstance.get(url, config);
    return response.data;
  }

  async post<T = any>(
    url: string,
    data?: any,
    params?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> =
      await this.axiosInstance.post(url, data, {...config, params});;
    return response.data;
  }

  async resendCode<T = any>(
  url: string,
  params?: any,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  const response: AxiosResponse<ApiResponse<T>> =
    await this.axiosInstance.post(url, null, {
      ...config,
      params,
    });

  return response.data;
}

  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> =
      await this.axiosInstance.put(url, data, config);
    return response.data;
  }

  async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> =
      await this.axiosInstance.patch(url, data, config);
    return response.data;
  }

  async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> =
      await this.axiosInstance.delete(url, config);
    return response.data;
  }

  async uploadFile<T = any>(
    url: string,
    file: File,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append("file", file);

    const response: AxiosResponse<ApiResponse<T>> =
      await this.axiosInstance.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress,
      });

    return response.data;
  }

  getInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

export const apiService = new ApiService();
export default apiService;
